import React from 'react';
import { Divider, Grid, IconButton, Snackbar, Typography } from '@mui/material';
import { CommonButtonV1, CommonTextFieldV1 } from 'gemlux-common-components';
import { styled as materialStyled } from '@mui/system';
import GemGPTIcon from '../../../assets/img/V1/gem-gpt-chat-icon.svg';
import CopyIcon from '../../../assets/img/V1/copy-icon.svg';
import { filterScrollbarStyles } from '../../../utils/styles';
import { IChatBotData } from '../../../redux/gemGPT/gemGPTslice';
import { ChatBotSender, handleCopy } from '../utils';

const CustomGridContainer = materialStyled(Grid)({
  width: '100%',
  height: '60vh',
  overflowY: 'auto',
  ...filterScrollbarStyles,
});

const CustomGridItem = materialStyled(Grid)({
  padding: '10px',
  borderRadius: '5px',
  marginBottom: '10px',
});

interface GemGPTChatGridProps {
  message: string;
  TimeStamp: string;
}

const GemGPTBotChatGrid = ({ message, TimeStamp }: GemGPTChatGridProps) => {
  const [open, setOpen] = React.useState(false);

  const handleClick = async () => {
    await handleCopy(message);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Grid item sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', maxWidth: '60%' }}>
      <Grid
        item
        sx={{
          mb: 'auto',
        }}
      >
        <Grid component="img" src={GemGPTIcon} alt="GempGPT icon" height="30px" width="30px" />
      </Grid>{' '}
      <Grid
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {' '}
        <Grid
          item
          sx={{
            display: 'flex',
            flexDirection: 'column',
            rowGap: '10px',
            backgroundColor: '#fff',
            padding: '12px',
            borderRadius: '0px 12px 12px 12px',
            mx: '10px',
          }}
        >
          <Grid
            item
            sx={{
              display: 'flex',
              gap: '5px',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            {' '}
            <Typography variant="textH6">GemGPT™</Typography>{' '}
            <Grid component="img" src={GemGPTIcon} alt="GempGPT icon" height="4px" width="4px" />
            <Typography variant="textBase" color="#6F7C8E">
              {TimeStamp}
            </Typography>
          </Grid>

          <Typography variant="textBase">{message}</Typography>
        </Grid>
        <IconButton onClick={handleClick}>
          <Grid component="img" src={CopyIcon} alt="GempGPT icon" height="20px" width="20px" />
        </IconButton>
      </Grid>
      {open && (
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          open={open}
          onClose={handleClose}
          autoHideDuration={1000}
          message="Copied to Clipboard"
          ContentProps={{
            sx: {
              textAlign: 'center',
              background: 'grey',
              color: 'black',
              borderRadius: '12px',
            },
          }}
        />
      )}
    </Grid>
  );
};

const UserChatGrid = ({ message, TimeStamp }: GemGPTChatGridProps) => (
  <Grid item sx={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'row' }}>
    <Grid
      sx={{
        display: 'flex',
        alignItems: 'center',
        maxWidth: '60%',
      }}
    >
      <Grid
        item
        sx={{
          display: 'flex',
          flexDirection: 'column',
          rowGap: '10px',
          backgroundColor: '#fff',
          padding: '12px',
          borderRadius: '12px 0px 12px 12px',
          mx: '10px',
        }}
      >
        <Grid
          item
          sx={{
            display: 'flex',
            gap: '5px',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <Typography variant="textH6">You </Typography>
          <Grid component="img" src={GemGPTIcon} alt="GempGPT icon" height="4px" width="4px" />
          <Typography variant="textBase" color="#6F7C8E">
            {TimeStamp}
          </Typography>
        </Grid>

        <Typography variant="textBase">{message}</Typography>
      </Grid>
    </Grid>
  </Grid>
);

interface GemGPTChatBoxProps {
  handleUserInput: (_message: string) => void;
  chatData: IChatBotData[];
}

function GemGPTChatBox({ handleUserInput, chatData }: GemGPTChatBoxProps) {
  const [message, setMessage] = React.useState('');

  const handleSendMessage = () => {
    handleUserInput(message);
    setMessage('');
  };

  const handleChanged = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(event.target.value);
  };

  return (
    <Grid>
      <CustomGridContainer container my={2}>
        {chatData.map((data) =>
          data.sender === ChatBotSender.BOT ? (
            <CustomGridItem item xs={12} sm={12} key={data.timeStamp}>
              <GemGPTBotChatGrid message={data.message} TimeStamp={data.timeStamp} />
            </CustomGridItem>
          ) : (
            <CustomGridItem item xs={12} sm={12} key={data.timeStamp}>
              <UserChatGrid message={data.message} TimeStamp={data.timeStamp} />
            </CustomGridItem>
          ),
        )}
      </CustomGridContainer>
      <Grid item xs={12} sm={12} px={4}>
        <Divider />
      </Grid>

      <Grid item xs={12} sm={12} sx={{ display: 'flex', flexDirection: 'row', gap: '5px', padding: '10px', mt: 2 }}>
        {' '}
        <CommonTextFieldV1
          fullWidth
          name="message"
          maxRows={8}
          multiline
          placeholder="Type your message here..."
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: '4px',
              backgroundColor: '#ffffff',
            },
          }}
          value={message}
          onChange={handleChanged}
        />
        <CommonButtonV1 onClick={handleSendMessage} disabled={!message}>
          <Typography variant="textBase" color="HighlightText">
            Send
          </Typography>
        </CommonButtonV1>
      </Grid>
    </Grid>
  );
}

export default GemGPTChatBox;
